import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, tap } from 'rxjs/operators';

import { ToastService } from '../../../modules/shared/services/toast-message.service';
import { RisksService } from '../../../services/risks/risks.service';
import { PagedResponse } from '../general/models/general';
import { ILibraryData, Library } from './models/Library';
import {
  IRiskCompliance,
  Risks,
  RisksFromVulnerability,
  RisksTasks,
} from './models/Risks';
import * as actions from './risks.actions';

@Injectable()
export class RisksEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly service: RisksService,
    private readonly toastService: ToastService,
  ) {}

  getRisksRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRisksRegister),
      exhaustMap(({ sortOptions, searchTerm, filters, pageNumber }) =>
        this.service
          .getRisksRegister({ ...sortOptions, searchTerm, filters, pageNumber })
          .pipe(
            map((pagedRisksRegister: PagedResponse<Risks>) => {
              return actions.getRisksRegisterSuccess({ pagedRisksRegister });
            }),
            catchError((error: any) =>
              of(actions.getRisksRegisterFailure(error)),
            ),
          ),
      ),
    ),
  );

  getRiskRegisterById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRisksRegisterById),
      exhaustMap((action) =>
        this.service.getRisksRegisterById(action.risksId).pipe(
          map((riskRegisterById: Risks) => {
            return actions.getRisksRegisterByIdSuccess({ riskRegisterById });
          }),
          catchError((error: any) =>
            of(actions.getRisksRegisterByIdFailure(error)),
          ),
        ),
      ),
    ),
  );

  getRisksVulnerabilityById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRiskVulnerabilityById),
      exhaustMap(({ id, sortOptions, searchTerm, filters, pageNumber }) =>
        this.service
          .getRisksVulnerabilityById(id, {
            ...sortOptions,
            searchTerm,
            filters,
            pageNumber,
          })
          .pipe(
            map(
              (
                pagedRiskVulnerabilityById: PagedResponse<RisksFromVulnerability>,
              ) => {
                return actions.getRiskVulnerabilityByIdSuccess({
                  pagedRiskVulnerabilityById,
                });
              },
            ),
            catchError((error: any) =>
              of(actions.getRiskVulnerabilityByIdFailure(error)),
            ),
          ),
      ),
    ),
  );

  getRisksTasksById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRiskTasksById),
      exhaustMap(
        ({ id, sortOptions, searchTerm, filters, pageNumber, pageSize }) =>
          this.service.getRisksTasksById(id, {
            ...sortOptions,
            searchTerm,
            filters,
            pageNumber,
            pageSize,
          })
          .pipe(
            map((pagedRiskTasksById: PagedResponse<RisksTasks>) => {
              return actions.getRiskTasksByIdSuccess({ pagedRiskTasksById });
            }),
            catchError((error: any) =>
              of(actions.getRiskTasksByIdFailure(error)),
            ),
          ),
      ),
    ),
  );

  addRiskRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.addRisksRegister),
      exhaustMap((action) =>
        this.service.addRiskRegister(action.riskRegister).pipe(
          map(() => {
            return actions.getRisksRegister({});
          }),
          catchError((error: any) =>
            of(actions.addRisksRegisterFailure(error)),
          ),
        ),
      ),
    ),
  );

  updateRiskRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateRiskRegister),
      exhaustMap((action) =>
        this.service.updateRiskRegister(action.riskId, action.newBody).pipe(
          map(() => {
            return actions.updateRiskRegisterSuccess({
              riskId: action.riskId,
              newBody: action.newBody,
            });
          }),
          catchError((error: any) =>
            of(actions.updateRiskRegisterFailure(error)),
          ),
        ),
      ),
    ),
  );

  updateRiskRegisterSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.updateRiskRegisterSuccess),
      tap(() => this.toastService.success('Risk updated successfully.')),
      map(({ riskId }) => actions.getRisksRegisterById({ risksId: riskId })),
    ),
  );

  deleteRiskRegister$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteRiskRegister),
      exhaustMap((action) =>
        this.service.deleteRiskRegister(action.riskId).pipe(
          map(() => {
            return actions.deleteRiskRegisterSuccess({ riskId: action.riskId });
          }),
          catchError((error: any) =>
            of(actions.deleteRiskRegisterFailure(error)),
          ),
        ),
      ),
    ),
  );

  getRisksLibrary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRisksLibrary),
      exhaustMap(({ sortOptions, searchTerm, pageNumber }) =>
        this.service
          .getRisksLibrary({ ...sortOptions, searchTerm, pageNumber })
          .pipe(
            map((pagedRisksLibrary: PagedResponse<Risks>) => {
              return actions.getRisksLibrarySuccess({ pagedRisksLibrary });
            }),
            catchError((error: any) =>
              of(actions.getRisksLibraryFailure(error)),
            ),
          ),
      ),
    ),
  );

  getRiskLibraryById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRisksLibraryById),
      exhaustMap((action) =>
        this.service.getRisksLibraryById(action.riskLibraryById).pipe(
          map((riskLibraryById: Library) => {
            return actions.getRisksLibraryByIdSuccess({ riskLibraryById });
          }),
          catchError((error: any) =>
            of(actions.getRisksLibraryByIdFailure(error)),
          ),
        ),
      ),
    ),
  );

  getRisksLibraryData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRisksLibraryData),
      exhaustMap(({ searchTerm, pageNumber }) =>
        this.service.getRisksLibraryData({ searchTerm, pageNumber }).pipe(
          map((pagedRisksLibraryData: PagedResponse<ILibraryData>) => {
            return actions.getRisksLibraryDataSuccess({
              pagedRisksLibraryData,
            });
          }),
          catchError((error: any) =>
            of(actions.getRisksLibraryDataFailure(error)),
          ),
        ),
      ),
    ),
  );

  getRiskComplianceById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getRisksComplianceById),
      exhaustMap((action) =>
        this.service.getRisksComplianceById(action.risksId).pipe(
          map((riskComplianceById: IRiskCompliance) => {
            return actions.getRisksComplianceByIdSuccess({
              riskComplianceById,
            });
          }),
          catchError((error: any) =>
            of(actions.getRisksComplianceByIdFailure(error)),
          ),
        ),
      ),
    ),
  );
}
